import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bot, AlertTriangle, ClipboardCheck, Sparkles, Play, Pause, X } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Step = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: string; // tailwind color classes for accent
  kicker: { en: string; bn: string };
  title: { en: string; bn: string };
  body: { en: string; bn: string };
  bullets: { en: string; bn: string }[];
  href: string;
  cta: { en: string; bn: string };
  mock: React.ReactNode;
};

const t = <T,>(lang: "en" | "bn", v: { en: T; bn: T }) => (lang === "bn" ? v.bn : v.en);

const WhatsAppMock = () => (
  <div className="rounded-2xl bg-[#0b141a] p-4 shadow-inner border border-white/5">
    <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 mb-2">WhatsApp · Live</div>
    <div className="space-y-2 text-sm">
      <div className="max-w-[80%] bg-white/10 text-white rounded-2xl rounded-tl-sm px-3 py-2">Hi doctor, appointment lagbe kal?</div>
      <div className="ml-auto max-w-[85%] bg-emerald-500/90 text-white rounded-2xl rounded-tr-sm px-3 py-2">
        Sure! Available: <b>10:30, 11:15, 12:00</b>. Reply with a time.
      </div>
      <div className="max-w-[60%] bg-white/10 text-white rounded-2xl rounded-tl-sm px-3 py-2">11:15</div>
      <div className="ml-auto max-w-[90%] bg-emerald-500/90 text-white rounded-2xl rounded-tr-sm px-3 py-2">
        ✅ Booked · Serial <b>#07</b> · Dr. Rahman · 11:15 AM · Gulshan Chamber
      </div>
    </div>
  </div>
);

const CareGapsMock = () => (
  <div className="rounded-2xl bg-white border border-orange-200 p-4 space-y-2">
    <div className="text-[10px] uppercase tracking-widest text-orange-500 mb-1">Detected today · 3 gaps</div>
    {[
      { n: "Rahima Begum", g: "HbA1c overdue by 4 months", sev: "high" },
      { n: "Karim Uddin", g: "BP follow-up missed", sev: "medium" },
      { n: "Ayesha Khan", g: "Lipid panel > 12 months", sev: "low" },
    ].map((r) => (
      <div key={r.n} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
        <div>
          <div className="font-medium text-gray-900">{r.n}</div>
          <div className="text-xs text-gray-500">{r.g}</div>
        </div>
        <span
          className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${
            r.sev === "high" ? "bg-red-100 text-red-700" : r.sev === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
          }`}
        >
          {r.sev}
        </span>
      </div>
    ))}
  </div>
);

const SpecialtyMock = () => (
  <div className="rounded-2xl bg-white border border-gray-200 p-4">
    <div className="text-[10px] uppercase tracking-widest text-blue-600 mb-2">Diabetes follow-up · 1 click</div>
    <div className="grid grid-cols-2 gap-2 text-xs">
      {["Type 2 DM", "HbA1c order", "Metformin 500 · 1+0+1", "Foot exam note", "Diet advice (BN)", "Follow-up 90d"].map((x) => (
        <div key={x} className="bg-blue-50 text-blue-900 rounded-lg px-2 py-1.5 border border-blue-100">{x}</div>
      ))}
    </div>
    <div className="mt-3 text-[11px] text-gray-500">Fills diagnosis, meds, investigations & follow-up in one tap.</div>
  </div>
);

const AiSuiteMock = () => (
  <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 p-5 text-white">
    <div className="text-[10px] uppercase tracking-widest text-white/70 mb-2">Ask AI · ⌘I</div>
    <div className="text-sm bg-white/10 rounded-xl p-3 mb-2">
      "Summarize Mr. Rahman's last 3 visits and suggest next follow-up."
    </div>
    <div className="text-xs bg-white/95 text-gray-800 rounded-xl p-3">
      Stable HTN on Amlodipine 5mg. BP trend improving (150→132). Next: 6-week check + lipid panel.
    </div>
    <div className="mt-3 flex gap-2 text-[10px]">
      <span className="bg-white/20 rounded-full px-2 py-0.5">Scribe</span>
      <span className="bg-white/20 rounded-full px-2 py-0.5">Diagnosis</span>
      <span className="bg-white/20 rounded-full px-2 py-0.5">Notes</span>
    </div>
  </div>
);

export default function ProductTour() {
  const { lang } = useLang();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef<number | null>(null);

  const steps: Step[] = useMemo(
    () => [
      {
        id: "wa",
        icon: Bot,
        tone: "text-emerald-600 bg-emerald-50 border-emerald-200",
        kicker: { en: "Step 1 · WhatsApp Agent", bn: "ধাপ ১ · হোয়াটসঅ্যাপ এজেন্ট" },
        title: { en: "Patients book themselves on WhatsApp", bn: "রোগীরা নিজেরাই WhatsApp-এ বুক করে" },
        body: {
          en: "The AI agent answers in Bangla or English, offers real open slots, and writes the appointment straight into your schedule with a serial number.",
          bn: "AI এজেন্ট বাংলা/ইংরেজিতে উত্তর দেয়, খালি স্লট দেখায় এবং সরাসরি সিরিয়াল নম্বরসহ অ্যাপয়েন্টমেন্ট বসায়।",
        },
        bullets: [
          { en: "Twilio & Meta Cloud API ready", bn: "Twilio ও Meta Cloud API সাপোর্ট" },
          { en: "Reschedule / cancel by reply", bn: "রিপ্লাই দিয়ে রিশিডিউল / ক্যানসেল" },
          { en: "Escalate to staff when unsure", bn: "সন্দেহে স্টাফের কাছে এসকেলেট" },
        ],
        href: "/ai-agent",
        cta: { en: "Open WhatsApp Agent", bn: "WhatsApp এজেন্ট দেখুন" },
        mock: <WhatsAppMock />,
      },
      {
        id: "gaps",
        icon: AlertTriangle,
        tone: "text-orange-600 bg-orange-50 border-orange-200",
        kicker: { en: "Step 2 · Care Gaps", bn: "ধাপ ২ · কেয়ার গ্যাপ" },
        title: { en: "Never miss an overdue follow-up", bn: "কখনো ফলো-আপ মিস হবে না" },
        body: {
          en: "The engine scans every patient for overdue screenings — HbA1c, BP, lipids, pediatric vaccines — and drafts a bilingual WhatsApp reminder you approve in one tap.",
          bn: "প্রতিটি রোগীর জন্য বকেয়া স্ক্রীনিং স্ক্যান করে এবং এক ট্যাপে অনুমোদনযোগ্য দ্বিভাষিক রিমাইন্ডার তৈরি করে।",
        },
        bullets: [
          { en: "Diabetes, HTN, pediatrics, women's health", bn: "ডায়াবেটিস, HTN, শিশু, নারী স্বাস্থ্য" },
          { en: "AI-drafted bilingual messages", bn: "AI দ্বিভাষিক মেসেজ" },
          { en: "Send via WhatsApp in one click", bn: "এক ক্লিকে WhatsApp-এ পাঠান" },
        ],
        href: "/care-gaps",
        cta: { en: "See Care Gaps engine", bn: "কেয়ার গ্যাপ দেখুন" },
        mock: <CareGapsMock />,
      },
      {
        id: "specialty",
        icon: ClipboardCheck,
        tone: "text-blue-600 bg-blue-50 border-blue-200",
        kicker: { en: "Step 3 · Specialty Templates", bn: "ধাপ ৩ · স্পেশালিটি টেমপ্লেট" },
        title: { en: "One-click consultations for your specialty", bn: "আপনার বিশেষত্বে এক ক্লিক কনসাল্টেশন" },
        body: {
          en: "Prebuilt presets for Diabetes, HTN, OB/GYN, Pediatrics and Endocrine fill diagnosis, medications, investigations and follow-up dates instantly.",
          bn: "ডায়াবেটিস, HTN, OB/GYN, শিশু, এন্ডোক্রিন প্রিসেট রোগনির্ণয়, ওষুধ, তদন্ত ও ফলো-আপ সঙ্গে সঙ্গে পূরণ করে।",
        },
        bullets: [
          { en: "5+ specialties out of the box", bn: "৫+ স্পেশালিটি রেডি" },
          { en: "Auto follow-up reminder scheduling", bn: "স্বয়ংক্রিয় ফলো-আপ রিমাইন্ডার" },
          { en: "Editable to match your style", bn: "আপনার স্টাইলে এডিটেবল" },
        ],
        href: "/specialty-templates",
        cta: { en: "Browse templates", bn: "টেমপ্লেট দেখুন" },
        mock: <SpecialtyMock />,
      },
      {
        id: "ai",
        icon: Sparkles,
        tone: "text-violet-600 bg-violet-50 border-violet-200",
        kicker: { en: "Step 4 · AI Suite", bn: "ধাপ ৪ · AI সুইট" },
        title: { en: "An AI co-pilot for the whole clinic", bn: "পুরো ক্লিনিকের জন্য AI কো-পাইলট" },
        body: {
          en: "Ambient Scribe writes Bengali SOAP notes, Diagnosis AI suggests differentials, and Ask-AI (⌘I) answers questions about any patient in seconds.",
          bn: "Ambient Scribe বাংলা SOAP নোট লিখে, Diagnosis AI ডিফারেন্সিয়াল দেয় এবং Ask-AI (⌘I) যেকোনো রোগীর প্রশ্নের উত্তর দেয়।",
        },
        bullets: [
          { en: "Ambient voice scribe (BN/EN)", bn: "ভয়েস স্ক্রাইব (বাংলা/ইংরেজি)" },
          { en: "Clinical decision support", bn: "ক্লিনিক্যাল সিদ্ধান্ত সহায়তা" },
          { en: "Global Ask-AI on ⌘I", bn: "⌘I চাপলেই Ask-AI" },
        ],
        href: "/scribe",
        cta: { en: "Explore the AI Suite", bn: "AI সুইট দেখুন" },
        mock: <AiSuiteMock />,
      },
    ],
    [],
  );

  useEffect(() => {
    if (!playing) return;
    timer.current = window.setTimeout(() => setActive((a) => (a + 1) % steps.length), 6000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [active, playing, steps.length]);

  const step = steps[active];
  const Icon = step.icon;

  return (
    <section id="product-tour" className="max-w-7xl mx-auto mt-24 md:mt-40 px-4">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 uppercase tracking-widest">
          <Play className="w-3 h-3" /> {t(lang, { en: "Interactive product tour", bn: "ইন্টার‍্যাক্টিভ প্রোডাক্ট ট্যুর" })}
        </div>
        <h2 className="mt-4 text-3xl md:text-5xl font-semibold text-[#111827] tracking-tight">
          {t(lang, { en: "See DoctorsX in 4 steps", bn: "৪ ধাপে DoctorsX দেখুন" })}
        </h2>
        <p className="mt-3 text-gray-600">
          {t(lang, {
            en: "Click any step — or let it auto-play — to see how the flagship modules work together.",
            bn: "যেকোনো ধাপে ক্লিক করুন — অথবা অটো-প্লে চলতে দিন — কীভাবে প্রধান মডিউলগুলো একসাথে কাজ করে দেখুন।",
          })}
        </p>
      </div>

      {/* Step rail */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-6">
        {steps.map((s, i) => {
          const SIcon = s.icon;
          const isActive = i === active;
          return (
            <button
              key={s.id}
              onClick={() => {
                setActive(i);
                setPlaying(false);
              }}
              className={`group relative text-left rounded-2xl border p-3 md:p-4 transition-all ${
                isActive ? "border-gray-900 bg-white shadow-sm" : "border-gray-200 bg-white/60 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex w-8 h-8 items-center justify-center rounded-lg border ${s.tone}`}>
                  <SIcon className="w-4 h-4" />
                </span>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">
                  {t(lang, { en: `0${i + 1}`, bn: `০${i + 1}` })}
                </div>
              </div>
              <div className="mt-2 text-sm font-semibold text-[#111827] line-clamp-1">{t(lang, s.kicker)}</div>
              {/* Progress bar */}
              <div className="absolute left-3 right-3 bottom-2 h-0.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  key={`${s.id}-${isActive}-${playing}`}
                  className={`h-full ${isActive ? "bg-gray-900" : "bg-transparent"}`}
                  style={
                    isActive && playing
                      ? { animation: "tourProgress 6s linear forwards" }
                      : isActive
                        ? { width: "100%" }
                        : undefined
                  }
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Stage */}
      <div className="grid md:grid-cols-2 gap-6 rounded-[2rem] border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 md:p-10">
        <div key={`copy-${step.id}`} className="animate-fade-in flex flex-col">
          <div className={`inline-flex items-center gap-2 self-start rounded-full border px-3 py-1 text-xs font-medium ${step.tone}`}>
            <Icon className="w-3.5 h-3.5" /> {t(lang, step.kicker)}
          </div>
          <h3 className="mt-4 text-2xl md:text-4xl font-semibold text-[#111827] tracking-tight">{t(lang, step.title)}</h3>
          <p className="mt-3 text-gray-600 leading-relaxed">{t(lang, step.body)}</p>
          <ul className="mt-5 space-y-2">
            {step.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${step.tone.split(" ")[0].replace("text-", "bg-")}`} />
                {t(lang, b)}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Link
              to={step.href}
              className="inline-flex items-center gap-2 rounded-full bg-[#111827] text-white px-4 py-2 text-sm font-medium hover:bg-black transition"
            >
              {t(lang, step.cta)} <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setActive((a) => (a - 1 + steps.length) % steps.length)}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-300"
              aria-label="Previous step"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setActive((a) => (a + 1) % steps.length)}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-300"
              aria-label="Next step"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPlaying((p) => !p)}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-300"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {playing
                ? t(lang, { en: "Pause", bn: "থামান" })
                : t(lang, { en: "Play", bn: "চালান" })}
            </button>
          </div>
        </div>

        <div key={`mock-${step.id}`} className="animate-fade-in">
          <div className="rounded-2xl bg-white border border-gray-200 shadow-xl p-4 md:p-5">
            <div className="flex items-center gap-1.5 mb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-400">doctorsx.com.bd</span>
            </div>
            {step.mock}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
